import React, { createContext, useState, useContext, useEffect, useMemo } from "react";
import axios from "axios";
import { getToken } from "../utils/tokenUtils";

// Create the salary band context
const SalaryBandMasterContext = createContext();

// Custom hook to use the salary band master context
export const useSalaryBand = () => useContext(SalaryBandMasterContext);

// Provider component to wrap the app
export const SalaryGradeMasterProvider = ({ children }) => {
  const [salaryBands, setSalaryBand] = useState([]);
  const API_URL = "https://hrmspayroll.prepstripe.com/api/masters/salary-bands"; // Replace with your actual API URL
  const token = getToken();
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    perPage: 10,
  });
  const [isLoading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchSalaryBands = async (page = 1) => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}?page=${page}`, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });

      const { data: paginationData } = response.data;
      const { data, current_page, last_page, per_page, total } = paginationData;

      setSalaryBand(data || []);

      setPagination({
        currentPage: current_page,
        totalPages: last_page,
        totalItems: total,
        perPage: per_page,
      });
      setLoading(false);
    } catch (error) {
      console.error("Error fetching salary band master:", error);
      setError(error.response?.data?.message || "Failed to fetch salary band.");
    } finally {
      setLoading(false);
    }
  };

  // Fetch salary band master from the API on load
  useEffect(() => {
    fetchSalaryBands();
  }, [API_URL, token]);

  // Add a new salary band  
  const addSalaryBand = async (newSalaryBand) => {
    try {
      const response = await axios.post(API_URL, newSalaryBand, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });
      setSalaryBand((prev) => [response.data?.data, ...prev]);
    } catch (error) {
      console.error("Error adding salary band:", error);
    }
  };

  // Update an existing salary band
  const updateSalaryBand = async (updatedSalaryBand) => {
    try {
      const response = await axios.put(`${API_URL}/${updatedSalaryBand.id}`, updatedSalaryBand, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });
      setSalaryBand((prev) =>
        prev.map((salaryBand) =>
          salaryBand.id === updatedSalaryBand.id ? { ...salaryBand, ...response.data?.data } : salaryBand
        )
      );
    } catch (error) {
      console.error("Error updating salary band master:", error);
    }
  };

  // Delete a salary band
  const deleteSalaryBand = async (id) => {
    try {
      await axios.delete(`${API_URL}/${id}`, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });
      setSalaryBand((prev) => prev.filter((salaryBand) => salaryBand.id !== id));
    } catch (error) {
      console.error("Error deleting salary band:", error);
    }
  };

  // Memoize the context value
  const value = useMemo(
    () => ({
      isLoading,
      error,
      pagination,
      salaryBands,
      fetchSalaryBands,
      addSalaryBand,
      updateSalaryBand,
      deleteSalaryBand,
    }),
    [salaryBands, isLoading, pagination] // Recalculate the value only when `salary bands` changes
  );

  return <SalaryBandMasterContext.Provider value={value}>{children}</SalaryBandMasterContext.Provider>;
};
